import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Search, Shield } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * RadioHostsAdminTab — two sections:
 *   1. Host sign-up applications (approve / deny)
 *   2. Restream permission toggles (grant / revoke per user)
 */
export default function RadioHostsAdminTab() {
  const [tab, setTab] = useState("signups");
  return (
    <div data-testid="admin-radio-hosts-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-black text-2xl text-white">Radio Hosts</h2>
      </div>
      <div className="flex gap-2 border-b border-white/10">
        <button onClick={() => setTab("signups")} className={`px-3 py-2 text-sm font-bold ${tab === "signups" ? "text-tv-accent border-b-2 border-tv-accent" : "text-white/60"}`} data-testid="admin-radio-tab-signups">Sign-ups</button>
        <button onClick={() => setTab("perms")} className={`px-3 py-2 text-sm font-bold ${tab === "perms" ? "text-tv-accent border-b-2 border-tv-accent" : "text-white/60"}`} data-testid="admin-radio-tab-perms">Restream permissions</button>
      </div>
      {tab === "signups" ? <SignupsList /> : <RestreamPerms />}
    </div>
  );
}

function SignupsList() {
  const [status, setStatus] = useState("pending");
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, denied: 0 });
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get(`/admin/radio/host-signups?status=${status}`),
        api.get(`/admin/radio/host-signup-counts`),
      ]);
      setApps(a.data || []); setCounts(c.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const decide = async (app, approve) => {
    try {
      await api.post(`/admin/radio/host-signups/${app.id}/${approve ? "approve" : "deny"}`, { note: note[app.id] || "" });
      toast.success(approve ? "Approved — user granted host access" : "Denied");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex gap-1.5">
          {[["pending", counts.pending], ["approved", counts.approved], ["denied", counts.denied], ["all", null]].map(([s, n]) => (
            <button
              key={s}
              data-testid={`admin-radio-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded ${status === s ? "bg-tv-accent text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >{s.charAt(0).toUpperCase() + s.slice(1)}{n != null ? ` (${n})` : ""}</button>
          ))}
        </div>
        <button onClick={load} className="ml-auto text-white/60 hover:text-white inline-flex items-center gap-1 text-sm"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading ? <div className="text-white/60 text-sm">Loading…</div> : (
        <div className="space-y-2" data-testid="admin-radio-signups-list">
          {apps.length === 0 && <div className="text-white/40 text-sm py-6 text-center">No {status !== "all" ? status : ""} applications.</div>}
          {apps.map((a) => (
            <div key={a.id} data-testid={`admin-signup-${a.id}`} className="bg-white/5 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-white">{a.show_name}</div>
                    <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      a.status === "pending" ? "bg-amber-500/25 text-amber-200" :
                      a.status === "approved" ? "bg-emerald-500/25 text-emerald-200" :
                      "bg-red-500/25 text-red-200"
                    }`}>{a.status}</span>
                  </div>
                  <div className="text-white/70 text-sm">by {a.display_name} · {a.email}{a.city ? ` · ${a.city}` : ""}</div>
                  {a.genres?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {a.genres.map((g) => <span key={g} className="text-[10px] bg-white/10 text-white/70 px-1.5 py-0.5 rounded">{g}</span>)}
                    </div>
                  )}
                  {a.bio && <p className="text-white/60 text-sm mt-2 whitespace-pre-wrap">{a.bio}</p>}
                  <div className="flex gap-3 mt-1 text-[11px]">
                    {a.demo_url && <a href={a.demo_url} target="_blank" rel="noreferrer" className="text-tv-accent hover:underline">Demo →</a>}
                    {a.social_url && <a href={a.social_url} target="_blank" rel="noreferrer" className="text-tv-accent hover:underline">Social →</a>}
                  </div>
                  {a.decision_note && <div className="text-white/50 text-[11px] mt-1 italic">Note: {a.decision_note}</div>}
                </div>
              </div>

              {a.status === "pending" && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    data-testid={`admin-signup-note-${a.id}`}
                    placeholder="Decision note (optional)"
                    value={note[a.id] || ""}
                    onChange={(e) => setNote({ ...note, [a.id]: e.target.value })}
                    className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"
                  />
                  <button
                    data-testid={`admin-signup-approve-${a.id}`}
                    onClick={() => decide(a, true)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
                  ><Check className="w-3 h-3" /> Approve</button>
                  <button
                    data-testid={`admin-signup-deny-${a.id}`}
                    onClick={() => decide(a, false)}
                    className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
                  ><X className="w-3 h-3" /> Deny</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RestreamPerms() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/radio/restream/admin/permissions");
      setUsers(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    try {
      await api.post(`/radio/restream/admin/permissions/${u.user_id}`, { can_restream: !u.can_restream });
      toast.success(`${u.can_restream ? "Revoked" : "Granted"} restream permission for ${u.email || u.display_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = users.filter((u) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (u.email || "").toLowerCase().includes(q) || (u.display_name || "").toLowerCase().includes(q) || (u.user_id || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-white/50" />
        <input
          data-testid="admin-restream-filter"
          placeholder="Filter by email, name, user id…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm"
        />
        <button onClick={load} className="text-white/60 hover:text-white inline-flex items-center gap-1 text-sm"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>
      <p className="text-white/50 text-xs">Shows admins, premium creators, and anyone already granted restream. Toggle the checkbox to grant / revoke multistream-to-socials access.</p>

      {loading ? <div className="text-white/60 text-sm">Loading…</div> : (
        <div className="space-y-1" data-testid="admin-restream-users">
          {visible.length === 0 && <div className="text-white/40 text-sm py-4 text-center">No users match.</div>}
          {visible.map((u) => (
            <label key={u.user_id} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded p-2 cursor-pointer" data-testid={`admin-restream-row-${u.user_id}`}>
              <input
                type="checkbox"
                checked={!!u.can_restream}
                onChange={() => toggle(u)}
                data-testid={`admin-restream-toggle-${u.user_id}`}
                className="accent-fuchsia-500 w-4 h-4"
              />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{u.display_name || u.email || u.user_id}</div>
                <div className="text-white/50 text-xs truncate">{u.email} · {u.role}</div>
              </div>
              {u.can_restream && <span className="bg-emerald-500/25 text-emerald-200 text-[10px] font-black uppercase px-2 py-0.5 rounded">Granted</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
